import { PriceChange } from "@crypto-trading/common/browser";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExchangeCode } from "@/lib/market-data/market-data.types";
import { getClientMarketDataService } from "@/lib/market-data/market-data-service";
import {
  CandleFetchErrorMeta,
  CandleInterval,
  CandlePoint,
  getExchangeCandlePageSize,
  toPriceChange,
  toPublicChartErrorMessage,
} from "@/lib/live-candles/candle.types";
import { CANDLE_INTERVAL_MS } from "@/lib/live-candles/candle.types";
import { getLiveCandleService } from "@/lib/live-candles/live-candle-service";
import { subscribeSharedLiveCandles } from "@/lib/live-candles/live-candle-subscription-registry";
import { normalizeCandleSymbol } from "@/lib/live-candles/candle-symbols";
import { resolveHistoricalCandles } from "@/lib/live-candles/resolve-historical-candles";
import { requiresApiCandleFallback, supportsBrowserDirectCandleWs } from "@/lib/live-candles/exchange-candle-routing";

const DEFAULT_PAGE_LIMIT = 500;
const DEFAULT_FOCUS_CANDLES = 60;

interface UseChartCandlesParams {
  userId: string;
  symbol: string;
  exchange: ExchangeCode;
  /** Tried in order when the preferred exchange has no candles. */
  exchanges?: ExchangeCode[];
  interval: CandleInterval;
  startTime: number | null;
  endTime: number | null;
  enabled?: boolean;
  pageLimit?: number;
  /** Extra candles to include before startTime on the initial fetch. */
  paddingCandles?: number;
  /** Alert charts retry the latest candles when the alert window is still empty. */
  allowRecentFallback?: boolean;
  /** Initial fetch window centered on this timestamp (open/trigger). */
  focusAnchorTime?: number | null;
  /** Initial fetch spans startTime through this timestamp (closed positions). */
  focusEndTime?: number | null;
  /** Visible candle count used to size the focusAnchorTime window. */
  initialFocusCandles?: number;
}

interface UseChartCandlesResult {
  priceChanges: PriceChange[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: CandleFetchErrorMeta | null;
  loadOlderCandles: () => Promise<void>;
  loadNewerCandles: () => Promise<void>;
  hasMoreHistory: boolean;
  hasMoreFuture: boolean;
  setFollowLive: (follow: boolean) => void;
  /** No exchange has candles because the token is not listed yet. */
  historicalUnavailable: boolean;
}

function mapError(error: unknown): CandleFetchErrorMeta {
  const raw = error instanceof Error ? error.message : "Unknown candle error";
  const message = toPublicChartErrorMessage(raw);
  const lower = raw.toLowerCase();
  if (lower.includes("not available") || lower.includes("not_found")) {
    return { code: "TOKEN_NOT_AVAILABLE", message };
  }
  if (lower.includes("429") || lower.includes("rate limit") || lower.includes("too many")) {
    return { code: "RATE_LIMIT", message: "Rate limited — try again in a moment" };
  }
  if (lower.includes("history") || lower.includes("retention")) {
    return { code: "HISTORY_OUT_OF_RANGE", message };
  }
  if (lower.includes("network") || lower.includes("fetch") || lower.includes("request failed")) {
    return { code: "NETWORK", message };
  }
  return { code: "UNKNOWN", message };
}

function mergeCandles(existing: CandlePoint[], incoming: CandlePoint[]): CandlePoint[] {
  const map = new Map<number, CandlePoint>();
  existing.forEach((candle) => map.set(candle.startTime, candle));
  incoming.forEach((candle) => map.set(candle.startTime, candle));
  return Array.from(map.values()).sort((a, b) => a.startTime - b.startTime);
}

function getInitialFetchRange(params: {
  startTime: number;
  endTime: number;
  interval: CandleInterval;
  paddingCandles: number;
  focusAnchorTime?: number | null;
  focusEndTime?: number | null;
  initialFocusCandles: number;
}): { fetchStartTime: number; fetchEndTime: number } {
  const intervalMs = CANDLE_INTERVAL_MS[params.interval];
  const paddingMs = params.paddingCandles * intervalMs;
  const now = Date.now();

  if (params.focusEndTime != null) {
    return {
      fetchStartTime: params.startTime - paddingMs,
      fetchEndTime: params.focusEndTime + paddingMs,
    };
  }

  if (params.focusAnchorTime != null) {
    const visible = params.initialFocusCandles;
    const barsBefore = Math.floor(visible * 0.35);
    const barsAfter = visible - barsBefore;
    return {
      fetchStartTime: params.focusAnchorTime - barsBefore * intervalMs - paddingMs,
      fetchEndTime: Math.min(
        params.focusAnchorTime + barsAfter * intervalMs + paddingMs,
        now
      ),
    };
  }

  return {
    fetchStartTime: params.startTime - paddingMs,
    fetchEndTime: Math.min(params.endTime, now),
  };
}

export function useChartCandles({
  userId,
  symbol,
  exchange,
  exchanges,
  interval,
  startTime,
  endTime,
  enabled = true,
  pageLimit = DEFAULT_PAGE_LIMIT,
  paddingCandles = 10,
  allowRecentFallback = false,
  focusAnchorTime = null,
  focusEndTime = null,
  initialFocusCandles = DEFAULT_FOCUS_CANDLES,
}: UseChartCandlesParams): UseChartCandlesResult {
  const [candles, setCandles] = useState<CandlePoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<CandleFetchErrorMeta | null>(null);
  const [historicalUnavailable, setHistoricalUnavailable] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [hasMoreFuture, setHasMoreFuture] = useState(true);
  const [followLive, setFollowLive] = useState(false);
  const [sourceExchange, setSourceExchange] = useState<ExchangeCode>(exchange);
  const isMountedRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const earliestStartRef = useRef<number | null>(null);
  const latestStartRef = useRef<number | null>(null);
  const liveUnsubscribeRef = useRef<(() => void) | null>(null);
  const sourceExchangeRef = useRef<ExchangeCode>(exchange);
  const fetchGenerationRef = useRef(0);

  const exchangeCandidates = useMemo(() => {
    if (exchanges && exchanges.length > 0) return exchanges;
    return [exchange];
  }, [exchange, exchanges]);
  const exchangeCandidatesKey = exchangeCandidates.join(",");
  const exchangeCandidatesRef = useRef(exchangeCandidates);
  exchangeCandidatesRef.current = exchangeCandidates;

  const normalizedSymbol = useMemo(
    () => normalizeCandleSymbol(sourceExchange, symbol),
    [sourceExchange, symbol]
  );

  const shouldEnable =
    enabled &&
    Boolean(userId) &&
    Boolean(symbol) &&
    Boolean(startTime) &&
    Boolean(endTime);

  const fetchInitial = useCallback(async () => {
    const generation = fetchGenerationRef.current + 1;
    fetchGenerationRef.current = generation;
    const isCurrent = () =>
      isMountedRef.current && fetchGenerationRef.current === generation;

    if (!shouldEnable || !startTime || !endTime) {
      setCandles([]);
      setError(null);
      setHistoricalUnavailable(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setHistoricalUnavailable(false);
    setHasMoreHistory(true);
    setHasMoreFuture(true);
    setFollowLive(false);

    const { fetchStartTime, fetchEndTime } = getInitialFetchRange({
      startTime,
      endTime,
      interval,
      paddingCandles,
      focusAnchorTime,
      focusEndTime,
      initialFocusCandles,
    });

    const marketData = getClientMarketDataService();
    const service = getLiveCandleService();
    const intervalMs = CANDLE_INTERVAL_MS[interval];

    try {
      const resolution = await resolveHistoricalCandles({
        symbol,
        candidates: exchangeCandidatesRef.current,
        interval,
        startTime: fetchStartTime,
        endTime: fetchEndTime,
        userId,
        pageLimit,
        allowRecentFallback,
        checkAvailable: (candidate, candleSymbol) =>
          marketData.checkTokenAvailable(candidate, candleSymbol),
        fetchCandles: (candidate, candleParams) =>
          service.fetchCandles(candidate, candleParams),
      });

      if (!isCurrent()) return;

      if (resolution.status === "candles") {
        sourceExchangeRef.current = resolution.exchange;
        setSourceExchange(resolution.exchange);
        setCandles(resolution.candles);
        setHistoricalUnavailable(false);
        setHasMoreHistory(true);
        earliestStartRef.current = resolution.candles[0]?.startTime ?? null;
        latestStartRef.current =
          resolution.candles[resolution.candles.length - 1]?.startTime ?? null;

        const latest = latestStartRef.current;
        if (
          !latest ||
          latest >= Date.now() - intervalMs ||
          requiresApiCandleFallback(resolution.exchange)
        ) {
          setHasMoreFuture(false);
        } else {
          setHasMoreFuture(true);
        }
        return;
      }

      setCandles([]);
      setHasMoreHistory(false);
      setHasMoreFuture(false);
      if (resolution.status === "rate_limited") {
        setHistoricalUnavailable(false);
        setError({
          code: "RATE_LIMIT",
          message: "Rate limited — try again in a moment",
        });
        return;
      }
      setError(null);
      setHistoricalUnavailable(true);
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  }, [
    shouldEnable,
    startTime,
    endTime,
    userId,
    symbol,
    interval,
    pageLimit,
    paddingCandles,
    allowRecentFallback,
    focusAnchorTime,
    focusEndTime,
    initialFocusCandles,
    exchangeCandidatesKey,
  ]);

  const loadOlderCandles = useCallback(async () => {
    if (!shouldEnable || loadingMoreRef.current || !hasMoreHistory) return;

    const earliestStart = earliestStartRef.current;
    if (!earliestStart) return;

    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    setError(null);

    try {
      const service = getLiveCandleService();
      const intervalMs = CANDLE_INTERVAL_MS[interval];
      const candleExchange = sourceExchangeRef.current;
      const limit = getExchangeCandlePageSize(candleExchange, pageLimit);
      const end = earliestStart - intervalMs;
      const start = end - intervalMs * limit;

      const older = await service.fetchCandles(candleExchange, {
        symbol,
        interval,
        startTime: start,
        endTime: end,
        limit,
        userId,
      });

      if (!isMountedRef.current) return;
      if (older.length === 0) {
        setHasMoreHistory(false);
        return;
      }

      setCandles((previous) => {
        const merged = mergeCandles(previous, older);
        earliestStartRef.current = merged[0]?.startTime ?? null;
        latestStartRef.current = merged[merged.length - 1]?.startTime ?? null;
        return merged;
      });

      if (older.length < limit) {
        setHasMoreHistory(false);
      }
    } catch (fetchError) {
      if (!isMountedRef.current) return;
      if (mapError(fetchError).code === "HISTORY_OUT_OF_RANGE") {
        setHasMoreHistory(false);
      }
    } finally {
      loadingMoreRef.current = false;
      if (isMountedRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [shouldEnable, hasMoreHistory, interval, pageLimit, symbol, userId]);

  const loadNewerCandles = useCallback(async () => {
    if (!shouldEnable || loadingMoreRef.current || !hasMoreFuture) return;

    const latestStart = latestStartRef.current;
    if (!latestStart) return;

    const intervalMs = CANDLE_INTERVAL_MS[interval];
    const now = Date.now();
    const latestAllowed = now - intervalMs;
    if (latestStart >= latestAllowed) {
      setHasMoreFuture(false);
      return;
    }

    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    setError(null);

    try {
      const service = getLiveCandleService();
      const candleExchange = sourceExchangeRef.current;
      const limit = getExchangeCandlePageSize(candleExchange, pageLimit);
      const start = latestStart + intervalMs;
      const maxEndByLimit = start + intervalMs * limit;
      const end = Math.min(maxEndByLimit, now);

      const newer = await service.fetchCandles(candleExchange, {
        symbol,
        interval,
        startTime: start,
        endTime: end,
        limit,
        userId,
      });

      if (!isMountedRef.current) return;
      if (newer.length === 0) {
        setHasMoreFuture(false);
        return;
      }

      setCandles((previous) => {
        const merged = mergeCandles(previous, newer);
        earliestStartRef.current = merged[0]?.startTime ?? null;
        latestStartRef.current = merged[merged.length - 1]?.startTime ?? null;
        return merged;
      });

      const latestAfter = newer[newer.length - 1]?.startTime ?? latestStart;
      if (latestAfter >= latestAllowed || newer.length < limit) {
        setHasMoreFuture(false);
      }
    } catch {
      if (!isMountedRef.current) return;
    } finally {
      loadingMoreRef.current = false;
      if (isMountedRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [shouldEnable, hasMoreFuture, interval, pageLimit, symbol, userId]);

  useEffect(() => {
    isMountedRef.current = true;
    void fetchInitial();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchInitial]);

  useEffect(() => {
    liveUnsubscribeRef.current?.();
    liveUnsubscribeRef.current = null;

    if (
      !shouldEnable ||
      !followLive ||
      !supportsBrowserDirectCandleWs(sourceExchange)
    ) {
      return;
    }

    liveUnsubscribeRef.current = subscribeSharedLiveCandles(
      sourceExchange,
      normalizedSymbol,
      interval,
      {
        onUpdate: (update) => {
          setCandles((previous) => {
            const merged = mergeCandles(previous, [update.candle]);
            earliestStartRef.current = merged[0]?.startTime ?? null;
            latestStartRef.current = merged[merged.length - 1]?.startTime ?? null;
            return merged;
          });
        },
      }
    );

    return () => {
      liveUnsubscribeRef.current?.();
      liveUnsubscribeRef.current = null;
    };
  }, [sourceExchange, normalizedSymbol, interval, followLive, shouldEnable]);

  const priceChanges = useMemo(
    () => candles.map((candle) => toPriceChange(candle)),
    [candles]
  );

  return {
    priceChanges,
    isLoading,
    isLoadingMore,
    error,
    loadOlderCandles,
    loadNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    setFollowLive,
    historicalUnavailable,
  };
}

